import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Check, X } from "lucide-react";

import Logo from "@/components/shared/Logo";
import StickyBar from "@/components/shared/StickyBar";
import { Badge } from "@/components/ui/badge";
import { pricingPlans } from "@/components/landing/pricing-data";
import {
  getPayfastMerchantNotice,
  shouldShowPayfastMerchantNotice,
} from "@/lib/billing-flow";
import {
  getOnboardingPromoCadenceOptions,
  getOnboardingPromoPlans,
  getPlanLabel,
} from "@/lib/onboarding-design-mode";
import { useI18n } from "@/lib/i18n";
import { buildIncludedPlanCard, onboardingGiftLabel } from "@/lib/onboarding-gift";

const PLAN_COLUMNS = ["free", "fun", "pro", "agent"];
const LIVE_PLAN_OVERRIDES = {
  free: {
    caption: "Start with outbound summaries and upgrade later when you want more control.",
    billedNow: "No card needed",
  },
  fun: {
    caption: "Translation for voice notes you send and receive",
    billedNow: "$1 billed monthly",
  },
  pro: {
    caption: "Use your own AI agent for WhatsApp summaries, messages and daily digests.",
    billedNow: "$10 billed monthly",
  },
  agent: {
    caption: "The higher-touch Min tier is coming soon.",
    billedNow: "Not available yet",
  },
};

const PLAN_COMPARISON_ROWS = [
  {
    label: "Outbound summaries",
    detail: "Summaries for the voice notes you send.",
    values: {
      free: true,
      fun: true,
      pro: true,
      agent: true,
    },
  },
  {
    label: "Inbound summaries",
    detail: "Summaries for voice notes you receive.",
    values: {
      free: false,
      fun: true,
      pro: true,
      agent: true,
    },
  },
  {
    label: "Full transcripts",
    detail: "Read inbound and outbound voice notes in full text.",
    values: {
      free: false,
      fun: true,
      pro: true,
      agent: true,
    },
  },
  {
    label: "Remove Min branding",
    detail: "Turn off the footer and branding markers on outputs.",
    values: {
      free: false,
      fun: false,
      pro: true,
      agent: true,
    },
  },
  {
    label: "Advanced controls",
    detail: "More control over how Min behaves chat by chat.",
    values: {
      free: false,
      fun: false,
      pro: true,
      agent: true,
    },
  },
  {
    label: "Higher-touch Min help",
    detail: "The more hands-on Min experience and support layer.",
    values: {
      free: false,
      fun: false,
      pro: false,
      agent: true,
    },
  },
];

function ComparisonStatusIcon({ included }) {
  if (included) {
    return (
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Check className="h-4 w-4" strokeWidth={2.5} />
      </span>
    );
  }

  return (
    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground/60">
      <X className="h-4 w-4" strokeWidth={2.25} />
    </span>
  );
}

function getLiveOnboardingPlans({ includedPlan = "", giftedDays, giftedMonths = 1, referrerName = "" } = {}) {
  const normalizedIncludedPlan = normalizeInitialPlan(includedPlan);
  if (normalizedIncludedPlan) {
    return [
      buildIncludedPlanCard(normalizedIncludedPlan, { giftedDays, giftedMonths, referrerName }),
      ...pricingPlans
        .filter((plan) => ![normalizedIncludedPlan, "free"].includes(plan.slug))
        .map((plan) => ({
          slug: plan.slug,
          name: plan.name,
          headlinePrice: plan.price,
          originalHeadlinePrice: "",
          suffix: plan.period,
          billedNow: LIVE_PLAN_OVERRIDES[plan.slug]?.billedNow || `${plan.price} billed monthly`,
          promoLabel: "",
          caption: LIVE_PLAN_OVERRIDES[plan.slug]?.caption || "",
          selectable: plan.selectable !== false,
          statusLabel: plan.statusLabel || "",
        })),
    ];
  }

  return pricingPlans
    .map((plan) => ({
      slug: plan.slug,
      name: plan.name,
      headlinePrice: plan.price,
      originalHeadlinePrice: "",
      suffix: plan.period,
      billedNow: LIVE_PLAN_OVERRIDES[plan.slug]?.billedNow || `${plan.price} billed monthly`,
      promoLabel: "",
      caption: LIVE_PLAN_OVERRIDES[plan.slug]?.caption || "",
      selectable: plan.selectable !== false,
      statusLabel: plan.statusLabel || "",
    }));
}

function getPromoOnboardingPlans(cadence, { includedPlan = "", giftedDays, giftedMonths = 1, referrerName = "" } = {}) {
  const normalizedIncludedPlan = normalizeInitialPlan(includedPlan) || "fun";
  return [
    buildIncludedPlanCard(normalizedIncludedPlan, { giftedDays, giftedMonths, referrerName }),
    ...getOnboardingPromoPlans(cadence).filter((plan) => plan.slug !== normalizedIncludedPlan),
  ];
}

function planMeta(slug) {
  return pricingPlans.find((plan) => plan.slug === slug) || null;
}

function normalizeInitialPlan(plan) {
  const normalized = String(plan || "").trim().toLowerCase();
  return ["free", "fun", "pro"].includes(normalized) ? normalized : "";
}

export default function PricingOffer({
  currentPlan = "free",
  initialPlan = "",
  initialCadence = "annual",
  includedPlan = "",
  giftedDays,
  giftedMonths = 1,
  referrerName = "",
  checkoutCurrencyNote = "",
  banner = null,
  offerMode = "promo",
  priceIds = {},
  onBack,
  onSkip,
  onContinue,
}) {
  const { tx } = useI18n();
  const isPromoMode = offerMode !== "live";
  const normalizedIncludedPlan = isPromoMode
    ? normalizeInitialPlan(includedPlan) || "fun"
    : normalizeInitialPlan(includedPlan);
  const [cadence, setCadence] = useState(initialCadence === "monthly" ? "monthly" : "annual");
  const [selectedPlan, setSelectedPlan] = useState(normalizeInitialPlan(initialPlan));
  const [pendingAction, setPendingAction] = useState("");
  const plans = useMemo(
    () => (
      isPromoMode
        ? getPromoOnboardingPlans(cadence, {
          includedPlan: normalizedIncludedPlan,
          giftedDays,
          giftedMonths,
          referrerName,
        })
        : getLiveOnboardingPlans({
          includedPlan: normalizedIncludedPlan,
          giftedDays,
          giftedMonths,
          referrerName,
        })
    ),
    [cadence, giftedDays, giftedMonths, isPromoMode, normalizedIncludedPlan, referrerName],
  );
  const cadenceOptions = useMemo(
    () => getOnboardingPromoCadenceOptions(cadence),
    [cadence],
  );
  const selectedPlanDetails = plans.find((plan) => plan.slug === selectedPlan) || null;
  const selectedPlanIsSelectable = selectedPlanDetails?.selectable !== false;
  const activePlanDetails = selectedPlanIsSelectable ? selectedPlanDetails : null;
  const payfastMerchantNotice = getPayfastMerchantNotice();
  const showPayfastMerchantNotice = (
    activePlanDetails?.slug !== normalizedIncludedPlan
    && shouldShowPayfastMerchantNotice(activePlanDetails?.slug)
  );
  const selectedPriceId = selectedPlanDetails && selectedPlan !== "free" && selectedPlan !== normalizedIncludedPlan
    ? (
      priceIds?.[selectedPlanDetails.slug]?.[
        isPromoMode ? cadence : "monthly"
      ] || ""
    )
    : "";
  const canContinueWithSelection = Boolean(
    activePlanDetails
      && (
        activePlanDetails.slug === "free"
        || activePlanDetails.slug === normalizedIncludedPlan
        || selectedPriceId
      )
  );
  const introCopy = normalizedIncludedPlan
    ? onboardingGiftLabel({ giftedDays, giftedMonths })
    : isPromoMode
      ? (
        currentPlan === "free"
          ? "Pick Free if you want to start there, or choose a discounted paid plan before onboarding ends."
          : `You are currently on ${getPlanLabel(currentPlan)}. Pick Free or lock in the paid plan you want before onboarding ends.`
      )
      : (
        currentPlan === "free"
          ? "Choose Free if you want to start there, or pick a paid plan for more control."
          : `You are currently on ${getPlanLabel(currentPlan)}. Choose the plan you want to switch to.`
      );
  const primaryLabel = !activePlanDetails
    ? "Choose a plan"
    : activePlanDetails.slug === normalizedIncludedPlan
      ? `Continue with ${getPlanLabel(normalizedIncludedPlan)}`
      : activePlanDetails.slug === "free"
        ? "Continue on Free"
        : `Continue with ${activePlanDetails.name}`;
  const isPending = pendingAction !== "";

  useEffect(() => {
    setCadence(initialCadence === "monthly" ? "monthly" : "annual");
  }, [initialCadence]);

  useEffect(() => {
    setSelectedPlan(normalizeInitialPlan(initialPlan || normalizedIncludedPlan));
  }, [initialPlan, normalizedIncludedPlan]);

  useEffect(() => {
    if (!selectedPlan) {
      return;
    }
    if (selectedPlan === "free") {
      return;
    }
    if (selectedPlanDetails?.selectable !== false) {
      return;
    }
    setSelectedPlan("");
  }, [selectedPlan, selectedPlanDetails]);

  function selectPlan(plan) {
    const nextPlan = plans.find((entry) => entry.slug === plan);
    if (!nextPlan || nextPlan.selectable === false) {
      return;
    }
    setSelectedPlan(plan);
  }

  async function handleContinue() {
    if (isPending || !activePlanDetails || !canContinueWithSelection) {
      return;
    }
    const continuingOnIncludedPlan = activePlanDetails.slug === normalizedIncludedPlan;
    const continuingOnFree = activePlanDetails.slug === "free";
    setPendingAction((continuingOnIncludedPlan || continuingOnFree) ? "skip" : "continue");
    try {
      if (continuingOnIncludedPlan || continuingOnFree) {
        await Promise.resolve(onSkip?.());
        return;
      }
      await Promise.resolve(onContinue?.({
        plan: activePlanDetails.slug,
        cadence,
        priceId: selectedPriceId,
      }));
    } finally {
      setPendingAction("");
    }
  }

  return (
    <div data-testid="pricing-offer" className="min-h-[100dvh] flex flex-col bg-background">
      <nav className="flex items-center justify-center px-5 py-4">
        <Logo size="sm" />
      </nav>

      <div className="flex-1 px-5 pb-44 md:pb-32">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4 md:space-y-5"
          >
            {isPromoMode ? (
              <div className="space-y-3">
                <Badge
                  variant="outline"
                  className="w-fit rounded-full border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-primary"
                >
                  {tx("Onboarding pricing")}
                </Badge>
                <p className="text-sm font-medium text-primary">
                  {normalizedIncludedPlan ? tx(onboardingGiftLabel({ giftedDays, giftedMonths })) : tx("Choose Free or lock in the onboarding discount now.")}
                </p>
              </div>
            ) : null}
            <div className="max-w-3xl">
              <h1 className="font-display text-3xl font-bold leading-tight md:text-5xl">
                {normalizedIncludedPlan ? tx("Fun is already on.") : isPromoMode ? tx("Choose how you want to start.") : tx("Choose your plan.")}
              </h1>
              <p className="mt-3 text-sm text-muted-foreground md:max-w-2xl md:text-base">
                {tx(introCopy)}
              </p>
              {checkoutCurrencyNote ? (
                <p className="mt-2 text-xs text-muted-foreground md:max-w-2xl">
                  {tx(checkoutCurrencyNote)}
                </p>
              ) : null}
            </div>
          </motion.div>

          <div className="space-y-6">
            {banner ? (
              <div
                className={`rounded-[1.5rem] border px-4 py-4 text-sm ${
                  banner.tone === "success"
                    ? "border-primary/20 bg-primary/5 text-foreground"
                    : banner.tone === "danger"
                      ? "border-destructive/20 bg-destructive/5 text-foreground"
                      : "border-border bg-card text-foreground"
                }`}
              >
                <p className="font-semibold">{tx(banner.title)}</p>
                <p className="mt-1 text-muted-foreground">{tx(banner.copy)}</p>
              </div>
            ) : null}

            {isPromoMode ? (
              <div className="grid grid-cols-2 gap-2 rounded-2xl bg-muted p-1 md:max-w-md">
                {cadenceOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setCadence(option.value)}
                    disabled={isPending}
                    className={`rounded-[1rem] px-4 py-3 text-sm font-semibold transition-colors ${
                      option.selected ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"
                    }`}
                  >
                    <span className="flex flex-col items-center gap-1">
                      <span>{tx(option.label)}</span>
                      {option.badge ? (
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] uppercase tracking-[0.14em] text-primary">
                          {tx(option.badge)}
                        </span>
                      ) : null}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}

            <div className={`grid gap-3 md:grid-cols-2 md:gap-4 ${plans.length >= 4 ? "xl:grid-cols-4" : "xl:grid-cols-3"}`}>
              {plans.map((plan) => {
                const selected = plan.slug === selectedPlan;
                const selectable = plan.selectable !== false;
                return (
                  <button
                    key={plan.slug}
                    type="button"
                    onClick={() => selectPlan(plan.slug)}
                    data-testid={`pricing-plan-${plan.slug}`}
                    className={`w-full rounded-[1.75rem] border p-5 text-left transition-all ${
                      selected && selectable
                        ? "border-primary bg-primary/5 shadow-[0_16px_40px_-30px_rgba(14,165,233,0.8)]"
                        : selectable
                          ? "border-border bg-card hover:border-primary/30 hover:bg-muted/20"
                          : "border-border bg-card opacity-65"
                    }`}
                    disabled={!selectable || isPending}
                    aria-pressed={selected && selectable}
                  >
                    <div className="relative min-w-0">
                      <Badge
                        variant="outline"
                        className={`absolute right-0 top-0 w-fit shrink-0 rounded-full px-3 py-1 shadow-none transition-none ${
                          plan.statusLabel
                            ? "border-border bg-background text-muted-foreground"
                            : plan.promoLabel
                            ? "border-primary/15 bg-primary/10 text-primary"
                            : "border-border bg-background text-muted-foreground"
                        }`}
                      >
                        {tx(plan.statusLabel || plan.promoLabel || "Live now")}
                      </Badge>
                      <div className="w-full pr-28">
                        <p className="font-display text-xl font-bold">{tx(plan.name)}</p>
                      </div>
                      <p className="mt-3 text-sm text-muted-foreground">{tx(plan.caption)}</p>
                    </div>
                    <div className="mt-4 flex items-end justify-between gap-4 md:flex-col md:items-start">
                      <div className="flex items-end gap-4">
                        {plan.originalHeadlinePrice ? (
                          <span className="font-display text-4xl font-bold text-muted-foreground/55 line-through">
                            {plan.originalHeadlinePrice}
                          </span>
                        ) : null}
                        <div className="flex items-end gap-1">
                          <span className="font-display text-4xl font-bold">{plan.headlinePrice}</span>
                          <span className="pb-1 text-sm text-muted-foreground">{tx(plan.suffix)}</span>
                        </div>
                      </div>
                      <p className="text-sm font-medium text-foreground">{tx(plan.billedNow)}</p>
                    </div>
                    {selected && selectable ? (
                      <div className="mt-4 inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-primary">
                        {tx("Selected")}
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {showPayfastMerchantNotice ? (
              <div className="rounded-[1.5rem] border border-border bg-card px-4 py-4 text-sm">
                <p className="font-semibold text-foreground">{tx(payfastMerchantNotice.title)}</p>
                <p className="mt-1 text-muted-foreground">{tx(payfastMerchantNotice.copy)}</p>
              </div>
            ) : null}

            <section className="space-y-4">
              <div className="max-w-2xl space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                  {tx("Full comparison")}
                </p>
                <h2 className="font-display text-2xl font-bold md:text-3xl">
                  {tx("See exactly what each plan unlocks.")}
                </h2>
              </div>

              <div className="space-y-3 md:hidden">
                {PLAN_COMPARISON_ROWS.map((row) => (
                  <section
                    key={row.label}
                    className="rounded-[1.5rem] border border-border bg-card p-4"
                  >
                    <div className="space-y-1">
                      <h3 className="text-sm font-semibold text-foreground">{tx(row.label)}</h3>
                      <p className="text-sm text-muted-foreground">{tx(row.detail)}</p>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                      {PLAN_COLUMNS.map((plan) => {
                        const included = row.values[plan];
                        const isSelected = plan === selectedPlan;
                        const meta = planMeta(plan);
                        const selectable = meta?.selectable !== false || plan === "free";
                        return (
                          <button
                            key={`${row.label}-${plan}`}
                            type="button"
                            onClick={() => selectPlan(plan)}
                            className={`rounded-[1.15rem] border px-3 py-3 text-left transition-colors ${
                              isSelected && selectable
                                ? "border-primary bg-primary/5 shadow-[0_10px_30px_-20px_rgba(14,165,233,0.8)]"
                                : selectable
                                  ? "border-border bg-background hover:bg-muted/30"
                                  : "border-border bg-background opacity-65"
                            }`}
                            disabled={!selectable || isPending}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <span className="flex flex-col text-left">
                                <span className="text-sm font-semibold text-foreground">
                                  {tx(getPlanLabel(plan))}
                                </span>
                                {meta?.statusLabel ? (
                                  <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                                    {tx(meta.statusLabel)}
                                  </span>
                                ) : null}
                              </span>
                              <ComparisonStatusIcon included={included} />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>

              <div className="hidden overflow-x-auto rounded-[1.75rem] border border-border bg-card md:block">
                <table className="min-w-[42rem] w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                      <th className="min-w-[14rem] px-4 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground md:px-5">
                        {tx("Feature")}
                      </th>
                      {PLAN_COLUMNS.map((plan) => {
                        const isSelected = plan === selectedPlan;
                        const meta = planMeta(plan);
                        const selectable = meta?.selectable !== false || plan === "free";
                        return (
                          <th
                            key={plan}
                            onClick={isPending ? undefined : () => selectPlan(plan)}
                            className={`px-4 py-4 text-center transition-colors md:px-5 ${
                              isSelected && selectable
                                ? "cursor-pointer bg-primary/5"
                                : selectable
                                  ? "cursor-pointer hover:bg-muted/30"
                                  : "bg-background/80"
                            }`}
                          >
                            <div className="flex flex-col items-center gap-1">
                              <span className="font-display text-lg font-bold text-foreground">
                                {tx(getPlanLabel(plan))}
                              </span>
                              {meta?.statusLabel ? (
                                <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                                  {tx(meta.statusLabel)}
                                </span>
                              ) : null}
                              {isSelected ? (
                                <Badge
                                  variant="outline"
                                  className="rounded-full border-primary/20 bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary"
                                >
                                  {tx("Selected")}
                                </Badge>
                              ) : null}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {PLAN_COMPARISON_ROWS.map((row, index) => (
                      <tr
                        key={row.label}
                        className={index === PLAN_COMPARISON_ROWS.length - 1 ? "" : "border-b border-border"}
                      >
                        <td className="px-4 py-4 align-top md:px-5">
                          <p className="text-sm font-semibold text-foreground">{tx(row.label)}</p>
                          <p className="mt-1 text-sm text-muted-foreground">{tx(row.detail)}</p>
                        </td>
                        {PLAN_COLUMNS.map((plan) => {
                          const included = row.values[plan];
                          const isSelected = plan === selectedPlan;
                          const meta = planMeta(plan);
                          const selectable = meta?.selectable !== false || plan === "free";
                          return (
                            <td
                              key={`${row.label}-${plan}`}
                              onClick={isPending ? undefined : () => selectPlan(plan)}
                              className={`px-4 py-4 text-center align-middle transition-colors md:px-5 ${
                                isSelected && selectable
                                  ? "cursor-pointer bg-primary/5"
                                  : selectable
                                    ? "cursor-pointer hover:bg-muted/20"
                                    : "bg-background/80"
                              }`}
                            >
                              <div className="flex items-center justify-center">
                                <ComparisonStatusIcon included={included} />
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      </div>

      <StickyBar
        label={tx(primaryLabel)}
        loading={pendingAction === "continue" || pendingAction === "skip"}
        loadingLabel={pendingAction === "skip" ? tx("Continuing…") : tx("Opening checkout…")}
        disabled={isPending || !canContinueWithSelection}
        onClick={() => {
          void handleContinue();
        }}
        secondaryLabel={tx("Back")}
        onSecondaryClick={onBack}
        secondaryDisabled={isPending}
        containerClassName="md:px-6"
        desktopVisible
      />
    </div>
  );
}
